import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getMoroccoNow } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { useDiscoverConfig } from "../../config";
import { useDiscoverItems } from "../../state/discover-context";
import { useTripActions } from "../../state/trip-context";
import { DEFAULT_TRIP_PEOPLE } from "../../state/trip-storage";
import {
  READY_TRIPS,
  buildTripFromReady,
  isReadyTripAvailable,
  readyTripCover,
} from "../../lib/ready-trips";
import { defaultRailHref } from "../discover-rail";
import { thumbSrcSet, thumbUrl } from "../map/thumb-url";
import type { DiscoverItem } from "../../types";

function tomorrowIso(today: string): string {
  const d = new Date(`${today}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

interface TripEmptyProps {
  readonly onCreate: () => void;
  /** Called with the new trip's id once a ready trip has been copied. */
  readonly onUseReady?: (tripId: string) => void;
}

/**
 * No trips yet: three ways in, most finished first.
 *
 * A ready trip copies a real itinerary the guest can then reshape; MaJourneys
 * AI writes one from a conversation; a blank trip is the last option, for
 * someone who already knows what they want. Ready trips only appear once the
 * catalogue has loaded and only with the stops it still carries.
 */
export function TripEmpty({ onCreate, onUseReady }: TripEmptyProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  const items = useDiscoverItems();
  const { createTripRaw } = useTripActions();
  const { railHref } = useDiscoverConfig();

  const bySlug = useMemo(() => {
    const map = new Map<string, DiscoverItem>(items.map((item) => [item.sourceSlug, item]));
    return (slug: string) => map.get(slug) ?? null;
  }, [items]);
  const ready = READY_TRIPS.filter((r) => isReadyTripAvailable(r, bySlug));

  const copyReady = (id: string) => {
    const template = READY_TRIPS.find((r) => r.id === id);
    if (!template) return;
    const trip = buildTripFromReady(template, bySlug, {
      name: t(template.nameKey),
      dayLabel: (n) => t("trip.day_label", { number: n }),
      // Starts tomorrow, so no stop is already behind us; each day's "Change
      // date or people" moves it.
      startDate: tomorrowIso(getMoroccoNow().date),
      people: DEFAULT_TRIP_PEOPLE,
    });
    createTripRaw(trip);
    onUseReady?.(trip.id);
  };

  const aiHref = (railHref ?? defaultRailHref)("chat", lang);

  return (
    <div className="mj-trip-glass-shell">
      <div className="mj-trip-glass-empty mj-trip-start">
        <h2>{t("trip.start.title")}</h2>
        <p>{t("trip.start.subtitle")}</p>

        {ready.length > 0 && (
          <ul className="mj-trip-start__ready" aria-label={t("trip.start.ready_label")}>
            {ready.map((r) => {
              const cover = readyTripCover(r, bySlug);
              const name = t(r.nameKey);
              return (
                <li key={r.id} className="mj-trip-start__card">
                  {cover ? (
                    <img
                      className="mj-trip-start__thumb"
                      src={thumbUrl(cover, 160)}
                      srcSet={thumbSrcSet(cover, [112, 160, 240])}
                      sizes="56px"
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span className="mj-trip-start__thumb" aria-hidden="true" />
                  )}
                  <span className="mj-trip-start__text">
                    <strong>{name}</strong>
                    <span>{t(r.lineKey)}</span>
                  </span>
                  <button
                    type="button"
                    className="mj-trip-start__use mj-press"
                    onClick={() => copyReady(r.id)}
                    aria-label={t("trip.start.use_named", { name })}
                  >
                    {t("trip.start.use")}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <a className="mj-trip-glass-empty__cta mj-trip-glass-empty__cta--ghost mj-trip-start__ai" href={aiHref}>
          <Icon name="ri-sparkling-2-fill" aria-hidden="true" />
          {t("trip.start.ask_ai")}
        </a>
        <button type="button" className="mj-trip-start__blank" onClick={onCreate}>
          {t("trip.start.blank")}
        </button>
      </div>
    </div>
  );
}
